import naturalCompare from 'string-natural-compare';

type GapCounts = Record<string, { performances: number; detections: number }>;

/**
 * Get the gap value for sorting purposes. If there is not a matched work, we use zero as the gap
 * value to match how sorting works in the table. If there is a matched work, we use the
 * performances and detections that were tallied up in the gapCounts object.
 * @param item - a {@link TrqkGapChannelAudio}
 * @returns the gap for sorting purposes
 */
const getSortingGap = (
    item: TrqkGapChannelAudio,
    gapCounts: GapCounts,
): number => {
    const matchedWorkId = item?.matched_work_id ?? null;
    const detections = item?.detection_count ?? 0;

    if (matchedWorkId === null) {
        return 0;
    }

    const gapCount = gapCounts?.[matchedWorkId] ?? null;

    if (gapCount === null) {
        // This should never happen because all matchedOptions have been added to gapCounts
        // in sortConfirmWorksData.
        return detections;
    }

    return gapCount.detections - gapCount.performances;
};

/**
 * Sort an array of {@link TrqkGapChannelAudio} to establish a default order in the ConfirmWorksTable.
 * @param audios - array of {@link TrqkGapChannelAudio}
 * @returns the input array after sorting. See comments for sorting logic.
 */
export const sortConfirmWorksData = (
    audios: TrqkGapChannelAudio[],
): TrqkGapChannelAudio[] => {
    const result = [...audios];

    /**
     * Object whose keys are matched work IDs and values are tallies of performances and detections.
     * Performances are only counted once for a given work ID.
     */
    const gapCounts: GapCounts = {};

    audios.forEach(
        ({
            matched_work_id,
            matched_work_performances,
            detection_count,
        }: TrqkGapChannelAudio) => {
            if (matched_work_id) {
                if (gapCounts[matched_work_id] === undefined) {
                    gapCounts[matched_work_id] = {
                        performances: matched_work_performances ?? 0,
                        detections: detection_count ?? 0,
                    };
                } else {
                    gapCounts[matched_work_id].detections +=
                        detection_count ?? 0;
                }
            }
        },
    );

    result.sort((itemA, itemB) => {
        const sortingGapA = getSortingGap(itemA, gapCounts);
        const sortingGapB = getSortingGap(itemB, gapCounts);

        /**
         * Sort larger gaps first
         */
        if (sortingGapA > sortingGapB) {
            return -1;
        }

        if (sortingGapA < sortingGapB) {
            return 1;
        }

        /**
         * If two items have the same sortingGap, sort them by detection title
         */
        return naturalCompare(
            itemA.detection_title ?? '',
            itemB.detection_title ?? '',
        );
    });

    return result;
};
